/**
 * Предметы на полу: мебель — в слое мебели, унитаз и ванна — в слое сантехники.
 *
 * Логика у них одна (центр, поворот, привязка к сетке), различается только слой,
 * поэтому стор один и создаётся фабрикой на каждый слой. Куда попадает новый
 * предмет, решает раздел каталога (`floorItemsStoreFor`), а кто владеет уже
 * стоящим — ищется по id (`floorItemOwner`): холсту и панелям всё равно, чья это вещь.
 */
import { create, type StoreApi, type UseBoundStore } from "zustand";
import {
	LayerKind,
	layerKindOfCatalogSection,
	uid,
	type CatalogRef,
	type CatalogSectionKey,
	type FloorItem,
} from "@/shared/plan";

export interface FloorItemsLayerState {
	items: Record<string, FloorItem>;
	visible: boolean;

	addFloorItem: (ref: CatalogRef, x: number, y: number, rotation?: number) => string;
	updateFloorItem: (id: string, patch: Partial<Omit<FloorItem, "id">>) => void;
	setFloorItemPosition: (id: string, x: number, y: number) => void;
	setFloorItemRotation: (id: string, rotation: number) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreFloorItems: (items: Record<string, FloorItem>) => void;
}

export type FloorItemsStore = UseBoundStore<StoreApi<FloorItemsLayerState>>;

const normalizeRotation = (rotation: number) => ((Math.round(rotation) % 360) + 360) % 360;

/** Стор предметов на полу одного слоя; `idPrefix` — чтобы id мебели и сантехники не путались. */
const createFloorItemsStore = (idPrefix: string): FloorItemsStore =>
	create<FloorItemsLayerState>((set) => ({
		items: {},
		visible: true,

		addFloorItem: (ref, x, y, rotation = 0) => {
			const id = uid(idPrefix);
			const item: FloorItem = { id, ref, x: Math.round(x), y: Math.round(y), rotation };
			set((s) => ({ items: { ...s.items, [id]: item }, visible: true }));
			return id;
		},

		updateFloorItem: (id, patch) =>
			set((s) => {
				const item = s.items[id];
				if (!item) return s;
				const next: FloorItem = { ...item, ...patch };
				next.rotation = normalizeRotation(next.rotation);
				return { items: { ...s.items, [id]: next } };
			}),

		setFloorItemPosition: (id, x, y) =>
			set((s) => {
				const item = s.items[id];
				if (!item) return s;
				return { items: { ...s.items, [id]: { ...item, x: Math.round(x), y: Math.round(y) } } };
			}),

		setFloorItemRotation: (id, rotation) =>
			set((s) => {
				const item = s.items[id];
				if (!item) return s;
				return { items: { ...s.items, [id]: { ...item, rotation: normalizeRotation(rotation) } } };
			}),

		setVisible: (visible) => set({ visible }),
		toggleVisible: () => set((s) => ({ visible: !s.visible })),

		restoreFloorItems: (items) => set({ items }),
	}));

/** Мебель на полу */
export const useFloorItemsStore = createFloorItemsStore("i");
/** Напольная сантехника: унитаз, ванна, душ, стиральная машина */
export const usePlumbingItemsStore = createFloorItemsStore("s");

/** Стор, куда попадает предмет раздела каталога: сантехника — в свой слой, остальное — в мебель. */
export const floorItemsStoreFor = (section: CatalogSectionKey): FloorItemsStore =>
	layerKindOfCatalogSection(section) === LayerKind.Plumbing
		? usePlumbingItemsStore
		: useFloorItemsStore;

/** Стор, в котором стоит предмет с этим id; null — такого предмета нет. */
export const floorItemOwner = (id: string): FloorItemsStore | null => {
	if (id in useFloorItemsStore.getState().items) return useFloorItemsStore;
	if (id in usePlumbingItemsStore.getState().items) return usePlumbingItemsStore;
	return null;
};
